import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Button, TextField, Typography, Snackbar, Alert,
  Paper, Divider, MenuItem, CircularProgress, Stack, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip,
} from "@mui/material";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import RefreshIcon    from "@mui/icons-material/Refresh";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { listCompanies } from "../../services/adminService";
import { API_BASE_URL } from "../../config/api";

const FONT = "'72', Arial, sans-serif";

const STATUS_CHIP_COLORS = {
  saved:          "success",
  xml_updated:    "info",
  already_exists: "default",
  received:       "warning",
  error:          "error",
};

function StatusChip({ status, t }) {
  const color = STATUS_CHIP_COLORS[status] || "default";
  const label = status ? (t(`sefaz.status.${status}`, { defaultValue: status }) || status) : status;
  return <Chip label={label} color={color} size="small" sx={{ fontFamily: FONT, fontSize: 11 }} />;
}

function schemaLabel(schema, t) {
  if (!schema) return "—";
  if (schema.startsWith("procNFe"))    return t("sefaz.schema.procNFe");
  if (schema.startsWith("resNFe"))     return t("sefaz.schema.resNFe");
  if (schema.startsWith("procCTe"))    return t("sefaz.schema.procCTe");
  if (schema.startsWith("resCTe"))     return t("sefaz.schema.resCTe");
  if (schema.startsWith("procEvento")) return t("sefaz.schema.procEvento");
  if (schema.startsWith("resEvento"))  return t("sefaz.schema.resEvento");
  return schema;
}

export default function XmlSefazPage() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [config, setConfig]       = useState(null);   // { tpAmb, ambiente }
  const [cnpj, setCnpj]           = useState("");
  const [lastNsu, setLastNsu]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [snack, setSnack]         = useState({ open: false, msg: "", severity: "success" });

  useEffect(() => {
    listCompanies().then(setCompanies).catch(() => {});
    fetch(`${API_BASE_URL}/admin/xml-sefaz/config`)
      .then((r) => r.json())
      .then(setConfig)
      .catch(() => {});
  }, []);

  const showSnack = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const fetchNsu = async (c) => {
    if (!c) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/xml-sefaz/nsu/${c}`);
      if (res.ok) { const d = await res.json(); setLastNsu(d.last_nsu); }
    } catch (_) {}
  };

  const handleChangeCnpj = (val) => { setCnpj(val); setLastNsu(null); setResult(null); fetchNsu(val); };

  const handleResetNsu = async () => {
    if (!cnpj) return;
    try {
      await fetch(`${API_BASE_URL}/admin/xml-sefaz/nsu/${cnpj}/reset`, { method: "PUT" });
      setLastNsu("0");
      showSnack(t("sefaz.distribute.nsuReset"));
    } catch (_) { showSnack(t("sefaz.distribute.nsuResetError"), "error"); }
  };

  const handleSubmit = async () => {
    if (!cnpj) return showSnack(t("sefaz.distribute.selectCompany"), "error");
    setLoading(true);
    setResult(null);
    try {
      const res  = await fetch(`${API_BASE_URL}/admin/xml-sefaz/distribute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cnpj }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || data?.error || "Erro ao consultar SEFAZ");
      setResult(data);
      setLastNsu(data.ultNSU || lastNsu);
      const saved = data.totalSaved || 0;
      const docs  = data.documents?.length || 0;
      showSnack(
        docs === 0
          ? t("sefaz.distribute.noNewDocs")
          : t("sefaz.distribute.docsReceived", { count: docs, saved }),
        docs === 0 ? "info" : "success"
      );
    } catch (e) {
      showSnack(e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const ambienteColor = config?.tpAmb === "1" ? "#c62828" : "#7b5800";

  return (
    <Box sx={{ p: 2 }}>
      <AdminPageHeader title="Distribuição DFe — SEFAZ" />

      <Paper variant="outlined" sx={{ mb: 2 }}>
        {/* Cabeçalho do card com badge de ambiente */}
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>
              {t("sefaz.distribute.cardTitle")}
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "text.secondary", mt: 0.5 }}>
              {t("sefaz.distribute.cardDesc")}
            </Typography>
          </Box>

          {config?.ambiente && (
            <Chip
              label={config.ambiente.toUpperCase()}
              size="small"
              sx={{
                flexShrink: 0,
                fontFamily: FONT, fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                backgroundColor: `${ambienteColor}18`,
                color: ambienteColor,
                border: `1px solid ${ambienteColor}55`,
                mt: 0.3,
              }}
            />
          )}
        </Box>

        <Divider />

        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <TextField
            select label={t("sefaz.distribute.empresa")} size="small"
            value={cnpj} onChange={(e) => handleChangeCnpj(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 360 }}
          >
            {companies.map((c) => (
              <MenuItem key={c.cnpj} value={c.cnpj} sx={{ fontFamily: FONT, fontSize: 13 }}>
                {c.cnpj} — {c.company_name}
              </MenuItem>
            ))}
          </TextField>

          {lastNsu !== null && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "text.secondary" }}>
                {t("sefaz.distribute.lastNsu")} <strong>{lastNsu}</strong>
              </Typography>
              <Tooltip title={t("sefaz.distribute.nsuResetTooltip")}>
                <IconButton size="small" onClick={handleResetNsu}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Mensagens SEFAZ */}
        {result?.messages?.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1 }}>
              {result.messages.map((m, i) => (
                <Typography key={i} sx={{ fontFamily: FONT, fontSize: 11, color: "text.secondary" }}>{m}</Typography>
              ))}
            </Box>
          </>
        )}

        {/* Tabela de documentos */}
        {result?.documents?.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700 }}>
                {t("sefaz.distribute.docsReceivedTitle", { count: result.documents.length })}
                {" — "}
                <span style={{ color: "#2e7d32" }}>{t("sefaz.distribute.savedCount", { count: result.totalSaved })}</span>
                {result.totalErrors > 0 && (
                  <span style={{ color: "#c62828", marginLeft: 8 }}>{t("sefaz.distribute.errorsCount", { count: result.totalErrors })}</span>
                )}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontFamily: FONT, fontSize: 11, fontWeight: 700, py: 0.5 } }}>
                    <TableCell>{t("sefaz.distribute.colNsu")}</TableCell>
                    <TableCell>{t("sefaz.distribute.colTipo")}</TableCell>
                    <TableCell>{t("sefaz.distribute.colChave")}</TableCell>
                    <TableCell>{t("sefaz.distribute.colStatus")}</TableCell>
                    <TableCell>{t("sefaz.distribute.colDetalhe")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.documents.map((d, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontFamily: FONT, fontSize: 11 }}>{d.nsu}</TableCell>
                      <TableCell sx={{ fontFamily: FONT, fontSize: 11 }}>{schemaLabel(d.schema, t)}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 10 }}>{d.accesskey || "—"}</TableCell>
                      <TableCell><StatusChip status={d.status} t={t} /></TableCell>
                      <TableCell sx={{ fontFamily: FONT, fontSize: 11, color: "text.secondary" }}>{d.message || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {result?.documents?.length === 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "text.secondary" }}>
                {t("sefaz.distribute.noNewDocsFound")}
              </Typography>
            </Box>
          </>
        )}
      </Paper>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CloudSyncIcon />}
          onClick={handleSubmit}
          disabled={loading || !cnpj}
          sx={{ textTransform: "none", fontFamily: FONT, fontSize: 13 }}
        >
          {loading ? t("sefaz.distribute.querying") : t("sefaz.distribute.distribute")}
        </Button>
      </Stack>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
